import { useEffect, useRef } from 'react';
import useDeepCompareMemoize from './useDeepCompareMemoize';

/**
 * Runs an effect on deep dependency changes while skipping the initial mount. Flips a ref on the first pass instead of calling back, keys the effect on the deep-memoized dependency array, and returns the supplied cleanup.
 *
 * @param callback - Effect body to run on updates.
 * @param dependencies - Dependencies compared deeply; defaults to an empty list.
 * @param cleanup - Teardown returned from the effect.
 * @returns Nothing.
 */
const useUpdateDeepCompareEffect = (
  callback: () => void,
  dependencies: unknown[] = [],
  cleanup?: () => void,
) => {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      callback();
    }
    return cleanup;
  }, useDeepCompareMemoize(dependencies));
};

export default useUpdateDeepCompareEffect;
